import { all, call, put, select, take, race, delay } from 'redux-saga/effects';

import { putAndWait } from 'modules/app/sagas';
import { simpleRequestsLoader } from 'modules/app/utils';

import { patchDashboardSettings, rollbackDashboardFields } from '../actions/dashboardCustomization';
import * as types from '../actionTypes';
import { BUFFERED_DELAY_MS } from '../constants';
import { getHiddenFieldsForDashboard } from '../selectors/dashboardPreferences';

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [types.PATCH_DASHBOARD_SETTINGS_REQUEST]: {
    notifications: {
      success: {
        id: 'dashboard.customization.update.success',
        defaultMessage: 'Dashboard settings updated successfully.',
      },
      failure: {
        id: 'http.error.message',
        defaultMessage: 'Oops! Something went wrong. Please try again or contact the administrator.',
      },
    },
  },
};

/**
 * Buffered saga for dashboard customization
 * Queues multiple field updates and sends them as a single PATCH request after 1500ms of inactivity
 */
function* patchDashboardSettingsBuffered() {
  while (true) {
    // Wait for the first action
    const firstAction = yield take(types.OPTIMISTIC_UPDATE_DASHBOARD_FIELD);

    // Store the initial state for rollback on failure
    const { dashboardType } = firstAction.payload;
    const initialHiddenFields = yield select(state => getHiddenFieldsForDashboard(state, { dashboardType }));

    let latestOptimisticAction = firstAction;
    let latestAction = firstAction;

    // Buffer subsequent actions
    while (true) {
      const { debounced, patch, nextAction } = yield race({
        debounced: delay(BUFFERED_DELAY_MS),
        patch: take(types.PATCH_DASHBOARD_SETTINGS_REQUEST),
        nextAction: take(types.OPTIMISTIC_UPDATE_DASHBOARD_FIELD),
      });

      if (!nextAction) {
        latestAction = debounced ?? patch;
        break;
      }

      latestOptimisticAction = nextAction;
    }

    // If a manual PATCH was sent, do not send another buffered request
    if (latestAction.type !== types.PATCH_DASHBOARD_SETTINGS_REQUEST) {
    // Send the buffered PATCH request with the latest hiddenFields
      const {
        payload: { userId, dashboardType: type, hiddenFields },
      } = latestOptimisticAction;
      const { error } = yield putAndWait(patchDashboardSettings(userId, type, hiddenFields));

      // On failure, rollback to initial state
      if (error) {
        yield put(rollbackDashboardFields(userId, type, initialHiddenFields));
      }
    }
  }
}

export default function* dashboardCustomizationSaga() {
  yield all([...simpleRequestsLoader(simpleRequests), call(patchDashboardSettingsBuffered)]);
}
